<template>
  <div id="menu-list">
    <Breadcrumbs
      :crumbs="[
        { name: getGlobals.info.school_name, link: `${getPathPrefix}/` },
        { name: getSectionName, link: `${getPathPrefix}/dining` },
      ]"
    />
    <LoadingIndicator v-show="loading" />
    <div
      v-show="!loading"
      ref="container"
      class="page-container"
    >
      <div
        v-if="getDiningPageFilters && getDiningPageFilters.length > 1"
        class="filter"
      >
        <select
          v-model="selectedFilter"
          name="filter"
          :aria-label="$t('pages.dining.diningFilter')"
          data-testid="dining-filter-dropdown"
          @change="filterCallback"
        >
          <option
            v-for="filter in getDiningPageFilters"
            :key="filter.id"
            :data-testid="`dining-filter-option-${filter.id}`"
            :value="filter.id"
          >
            {{ filter.name.toUpperCase() }}
          </option>
        </select>
      </div>
      <menu-item
        v-for="item in menus"
        :key="item.id"
        data-testid="dining-menu-item"
        :item="item"
      />
    </div>
    <div class="dining-legal page-container">
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="page-container-item"
        v-html="getDiningPageDisclaimer.fda_statement"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>
  </div>
</template>

<script>
import { mapGetters, useStore } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import concat from 'lodash/concat';
import { useScroll, useThrottleFn } from '@vueuse/core';
import { watch } from 'vue';
import Breadcrumbs from '../../components/global/Breadcrumbs.vue';
import LoadingIndicator from '../../components/global/LoadingIndicator.vue';
import MenuItem from '../../components/pages/dining/menu-item';
import pageHelpers from '../page-mixins';
import getLocale from '../../helpers/locale';
import { fetchFiltersAndDisclaimer, fetchMenus, formatDate } from '../../helpers/section/dining.async';

export default defineNuxtComponent({
  name: 'Menus',
  components: {
    MenuItem,
    Breadcrumbs,
    LoadingIndicator,
  },
  mixins: [pageHelpers],
  beforeRouteUpdate(to, from, next) {
    if (import.meta.client && to.query.filter !== from.query.filter) {
      this.fetchFiltersAndMenus(to.query, document.cookie);
      next();
    } else {
      next();
    }
  },
  async setup() {
    const store = useStore();
    const { data } = await useAsyncData('dining', () =>
      fetchFiltersAndDisclaimer({ store, pageQuery: useRoute().query, api: useNuxtApp().$axios }),
    );
    useHead({
      title: store.getters.getSectionName,
    });
    return data.value;
  },
  data() {
    return {
      loading: false,
      selectedFilter: null,
      menus: [],
      links: null,
      loadAdditionalMenu: false,
      stopInfiniteScrollWatch: null,
    };
  },
  computed: {
    ...mapGetters([
      'getDiningApi',
      'getLinks',
      'getGlobals',
      'getPathPrefix',
      'getCustomSections',
      'getSectionName',
      'getDiningPageFilters',
      'getDiningPageDisclaimer',
    ]),
    ...mapGetters('translation', ['getLanguage']),
    hasMoreMenus() {
      return Boolean(this.menus.length && this.links?.next);
    },
  },
  async mounted() {
    this.setupInfiniteScroll();
  },
  beforeUnmount() {
    if (this.stopInfiniteScrollWatch) {
      this.stopInfiniteScrollWatch();
    }
  },
  methods: {
    formatDate,
    setupInfiniteScroll() {
      const SCROLL_THRESHOLD = 100;
      const { y } = useScroll(window);

      const checkScrollPosition = useThrottleFn(() => {
        if (!this.hasMoreMenus || this.loadAdditionalMenu) {
          return;
        }

        const container = this.$refs.container;
        const containerBottom = container.getBoundingClientRect().bottom;
        const viewportHeight = window.innerHeight;
        const distanceFromBottom = containerBottom - viewportHeight;

        if (distanceFromBottom < SCROLL_THRESHOLD) {
          this.addMenuResults();
        }
      }, 100);

      this.stopInfiniteScrollWatch = watch(y, checkScrollPosition, { immediate: true });
    },
    /* used for SSG only */
    async fetchFiltersAndMenus(query, cookie) {
      this.loading = true;
      try {
        const langCode = getLocale(this.$store, cookie);
        if (!isEmpty(langCode) && langCode !== 'en') {
          const { menus, selectedFilter, links } = await fetchFiltersAndDisclaimer({
            store: this.$store,
            pageQuery: query,
            api: this.$axios,
          });
          this.selectedFilter = selectedFilter;
          this.menus = menus;
          this.links = links;
          this.loading = false;
          return;
        }
        if (!isEmpty(query)) {
          const { menus, selectedFilter, links } = await fetchMenus({
            store: this.$store,
            pageQuery: query,
            api: this.$axios,
          });
          this.selectedFilter = selectedFilter;
          this.menus = menus;
          this.links = links;
          this.loading = false;
        }
      } catch (error) {
        console.error('Error in fetchFiltersAndMenus:', error);
      } finally {
        this.loading = false;
      }
    },
    async addMenuResults() {
      if (!this.links.next) return;
      try {
        if (this.loadAdditionalMenu) return;
        this.loadAdditionalMenu = true;
        const url = new URL(this.links.next);
        const newPath = url.pathname.concat(url.search);
        const { data } = await this.$axios.get(newPath);
        const nextMenuPage = data.menus;
        this.links = data.meta.links;
        this.menus = concat(this.menus, nextMenuPage);
      } catch (e) {
        console.error('Error loading additional menu results:', {
          message: e.message,
          status: e.response?.status,
          statusText: e.response?.statusText,
        });
      } finally {
        this.loadAdditionalMenu = false;
      }
    },
  },
});
</script>

<style scoped lang="scss">
#menu-list {
  .menu-filter {
    float: right;
  }
  .menus {
    max-width: 100%;
    max-height: 300px;
    overflow: hidden;
    border-bottom: 1px solid #ddd;
  }
  .page-container {
    padding-bottom: 0;
  }
  .dining-legal {
    line-height: 17px;
    color: #555;
    padding-bottom: 30px;
    .page-container-item {
      border-bottom: none;
    }
    :deep() {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        margin-bottom: 15px;
        font-weight: 700;
        line-height: 1;
        color: var(--heading-color);
      }
      a {
        color: var(--links-color);
      }
      p {
        margin-bottom: 15px;
        font-weight: 400;
        font-size: 13px;
      }
      li,
      blockquote,
      pre {
        font-size: 13px;
      }
    }
  }
}
</style>
